import { Controller } from "@hotwired/stimulus"
import { sleep } from "../lib/helpers"

export default class extends Controller {
  static values = {
    squareSize: { type: Number, default: 24 },
    gap: { type: Number, default: 4 },
    minSquares: { type: Number, default: 1 },
    maxSquares: { type: Number, default: 10 },
    baseColor: { type: String, default: "color-mix(in srgb, var(--color-azure-400) 14%, transparent)" },
    addColor: { type: String, default: "var(--color-astral-300)" },
    tickMin: { type: Number, default: 8000 },
    tickMax: { type: Number, default: 20000 },
    fadeDuration: { type: Number, default: 800 }
  }

  connect() {
    this.#alive = true
    this.#columns = []
    this.#reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this.element.setAttribute("aria-hidden", "true")
    Object.assign(this.element.style, {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-end",
      justifyContent: "space-between",
      gap: `${this.gapValue}px`,
      width: "100%",
      boxSizing: "border-box",
      height: `${this.#rootHeight + this.gapValue}px`,
      paddingBottom: `${this.gapValue}px`
    })

    this.#sync()
    this.#resizeObserver = new ResizeObserver(() => this.#sync())
    this.#resizeObserver.observe(this.element)
    if (!this.#reduced) {
      document.addEventListener("visibilitychange", this.#onVisibility)
    }
  }

  disconnect() {
    this.#alive = false
    this.#resizeObserver?.disconnect()
    document.removeEventListener("visibilitychange", this.#onVisibility)
    this.element.replaceChildren()
  }

  #alive = false
  #columns
  #reduced
  #resizeObserver
  #paused = document.hidden

  get #cell() {
    return this.squareSizeValue + this.gapValue
  }

  get #rootHeight() {
    const n = this.maxSquaresValue
    return n * this.squareSizeValue + (n - 1) * this.gapValue
  }

  get #targetColumns() {
    const width = this.element.clientWidth
    return Math.max(1, Math.floor((width + this.gapValue) / this.#cell))
  }

  get #tickFloor() {
    return Math.max(this.tickMinValue, this.fadeDurationValue + 200)
  }

  #onVisibility = () => {
    this.#paused = document.hidden
  }

  #sync() {
    if (!this.#alive) return
    const target = this.#targetColumns
    while (this.#columns.length < target) this.#addColumn()
    while (this.#columns.length > target) this.#removeColumn()
  }

  #addColumn() {
    const el = document.createElement("div")
    Object.assign(el.style, {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      gap: `${this.gapValue}px`,
      width: `${this.squareSizeValue}px`,
      height: "100%"
    })

    const column = { el, alive: true, busy: false, count: 0 }

    const seed = this.#randomInt(this.minSquaresValue, this.maxSquaresValue)
    for (let i = 0; i < seed; i++) {
      el.appendChild(this.#makeSquare())
      column.count++
    }

    this.element.appendChild(el)
    this.#columns.push(column)
    if (!this.#reduced) this.#loop(column)
  }

  #removeColumn() {
    const column = this.#columns.pop()
    if (!column) return
    column.alive = false
    column.el.remove()
  }

  async #loop(column) {
    await sleep(this.#random(0, this.tickMaxValue))
    while (this.#alive && column.alive) {
      if (!this.#paused && !column.busy) this.#tick(column)
      await sleep(this.#random(this.#tickFloor, this.tickMaxValue))
    }
  }

  #tick(column) {
    const min = this.minSquaresValue
    const max = this.maxSquaresValue
    const fill = max === min ? 0 : (column.count - min) / (max - min)

    let addWeight = 1 - fill
    let removeWeight = fill
    const idleWeight = 0.5
    if (column.count <= min) removeWeight = 0
    if (column.count >= max) addWeight = 0

    const roll = Math.random() * (addWeight + removeWeight + idleWeight)
    if (roll < addWeight) {
      this.#addSquare(column)
    } else if (roll < addWeight + removeWeight) {
      this.#removeSquare(column)
    }
  }

  #addSquare(column) {
    const square = this.#makeSquare()
    square.style.backgroundColor = this.addColorValue
    square.style.opacity = "0"
    square.style.transform = "translateY(-6px)"
    square.style.transition = `opacity 250ms ease-out, transform 400ms ease-out, background-color ${this.fadeDurationValue}ms ease-out`
    column.el.insertBefore(square, column.el.firstChild)
    column.count++
    column.busy = true

    void square.offsetWidth
    square.style.opacity = "1"
    square.style.transform = "translateY(0)"
    square.style.backgroundColor = this.baseColorValue
    setTimeout(() => {
      square.style.transition = `opacity ${this.fadeDurationValue}ms linear`
      column.busy = false
    }, this.fadeDurationValue)
  }

  #removeSquare(column) {
    const square = column.el.firstChild
    if (!square) return
    column.count--
    column.busy = true
    square.style.opacity = "0"
    setTimeout(() => {
      square.remove()
      column.busy = false
    }, this.fadeDurationValue)
  }

  #makeSquare() {
    const square = document.createElement("div")
    Object.assign(square.style, {
      width: `${this.squareSizeValue}px`,
      height: `${this.squareSizeValue}px`,
      flex: "0 0 auto",
      borderRadius: "3px",
      backgroundColor: this.baseColorValue,
      transition: `opacity ${this.fadeDurationValue}ms linear`
    })
    return square
  }

  #random(lo, hi) {
    return lo + Math.random() * (hi - lo)
  }

  #randomInt(lo, hi) {
    return Math.floor(lo + Math.random() * (hi - lo + 1))
  }
}
